import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import Table from "@/common/elements/Table";
import AliasAddForm from "@/components/BookingsDetails/PersonalInformationDetails/AliasAddForm";
import {
  InmateDetailsTableContainer,
  TableHeader,
} from "../../InmateDetails/styles";
import Button from "@/common/elements/Button";
import { Popconfirm } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import CustomModal from "../../CustomModal";
import API_ENDPOINTS from "@/util/api";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import { hasCheckSecurity } from "@/util/common_function";

export const AliasesList = forwardRef(
  ({
    showQuickAddForm = true,
    selectedAliases,
    associates,
    onAliasesChange,
  }) => {
    const aliasFormRef = useRef();
    const dispatch = useDispatch();
    const { bookings, bookingsFieldsEnabled } = useSelector(
      (state) => state.bookings
    );

    const initialAliases = associates
      ? (selectedAliases || []).map((a) => ({
          ...a,
          id: a.id || uuidv4(),
        }))
      : selectedAliases || bookings?.bio?.[0]?.bioAliases || [];

    const [aliasData, setAliasData] = useState(initialAliases);
    const [showModal, setShowModal] = useState(false);
    const [empty, setEmpty] = useState(false);

    const [selectAlias, setSelectAlias] = useState({
      lName: "",
      fName: "",
      mName: "",
      suffix: "",
      dob: "",
      ssn: "",
      questionedIdentity: "",
    });
    const [showEdit, setShowEdit] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);

    // When bookings or selectedAliases change, update aliasData accordingly.
    useEffect(() => {
      if (associates) {
        setAliasData(
          (selectedAliases || []).map((a) => ({
            ...a,
            id: a.id || uuidv4(),
          }))
        );
      } else {
        let aliases = bookings?.bio?.[0]?.bioAliases || [];
        aliases = aliases.map((al) => ({
          ...al,
          lName: al.lName,
          fName: al.fName,
          mName: al.mName,
          suffix: al.suffix,
          dob: al.dob,
          ssn: al.ssn,
          questionedIdentity: al.questionedIdentity,
          id: al.id || uuidv4(),
        }));
        setAliasData(aliases);
      }
    }, [selectedAliases, bookings, associates]);

    // Use a ref to store previous aliasData.
    const prevAliasDataRef = useRef();
    useEffect(() => {
      const prev = prevAliasDataRef.current;
      const stripId = (alias) => {
        const { id, ...rest } = alias;
        return rest;
      };

      const prevDataWithoutId = JSON.stringify(
        prevAliasDataRef.current?.map(stripId)
      );
      const currentDataWithoutId = JSON.stringify(aliasData.map(stripId));

      if (prevDataWithoutId === currentDataWithoutId) {
        return;
      }
      prevAliasDataRef.current = aliasData;
      // Only update if associates mode is true.
      if (associates) {
        if (typeof onAliasesChange === "function") {
          onAliasesChange(aliasData);
        }
      } else {
        const bioAliases = bookings?.bio?.[0]?.bioAliases;
        if (JSON.stringify(bioAliases) !== JSON.stringify(aliasData)) {
          dispatch(
            setBookings({
              bio: [
                {
                  ...bookings?.bio?.[0],
                  bioAliases: aliasData,
                },
              ],
            })
          );
        }
      }
    }, [aliasData]);

    // Toggle modal visibility.
    const handleAssociateseModal = () => {
      setShowModal((prev) => !prev);
    };

    // When user clicks Add, clear selection and open modal.
    const handleAddAssociates = () => {
      setSelectAlias({
        lName: "",
        fName: "",
        mName: "",
        suffix: "",
        dob: "",
        ssn: "",
        questionedIdentity: "",
      });
      setShowEdit(false);
      handleAssociateseModal();
    };

    // When a table row is clicked, set the selected alias and enable edit/delete.
    const selectAliasDataFn = (value) => {
      setSelectAlias({ ...value });
      setShowEdit(true);
      setEmpty(() => true);
    };

    // When user clicks Details.
    const editAlias = () => {
      setShowEdit(true);
      handleAssociateseModal();
    };

    // When user clicks Delete, remove the selected alias.
    const deleteAlias = () => {
      setAliasData((prev) => prev.filter((p) => p?.id !== selectAlias?.id));
      const url = API_ENDPOINTS.bioAliases + "/id/" + selectAlias?.bioAliasId;
      dispatch(setDeleteAPis(url));
      closeModal();
    };

    const closeModal = () => {
      setSelectAlias({
        lName: "",
        fName: "",
        mName: "",
        dob: "",
        ssn: "",
        suffix: "",
        questionedIdentity: false,
      });
      setShowModal(false);
      setShowEdit(false);
    };

    // Save alias from the form.
    const saveClick = () => {
      aliasFormRef?.current
        ?.validateFields()
        .then((values) => {
          values.dob = values?.dob
            ? dayjs(values.dob).format("MM/DD/YYYY")
            : "";
          if (showEdit) {
            setAliasData((prev) =>
              prev.map((p) =>
                p?.id === selectAlias?.id
                  ? { ...values, bioAliasId: selectAlias.bioAliasId, id: p.id }
                  : p
              )
            );
          } else {
            setAliasData((prev) => [...prev, { ...values, id: uuidv4() }]);
          }
          closeModal();
          aliasFormRef?.current?.resetForm();
        })
        .catch((err) => {
          console.error(err);
        });
    };

    // Enable buttons only if bookingsFieldsEnabled is true.
    const isEnabled = bookingsFieldsEnabled;
    useEffect(() => {
      if (showEdit) {
        let showSave = hasCheckSecurity(
          Security_Types.functionality,
          Entity_types.subModule,
          LABELS_CAPITAL.MODIFY,
          MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
          SUB_MODULES_LIST_WITH_ID.Arrest_Report
        );
        if (showSave) {
          setIsDisabled(false);
        } else {
          setIsDisabled(true);
        }
      } else {
        setIsDisabled(false);
      }
    }, [showEdit]);
    useEffect(() => {
      console.log({ isDisabled });
    }, [isDisabled]);
    return (
      <>
        {showQuickAddForm && (
          <AliasAddForm
            formData={empty ? null : selectAlias}
            isQuickAdd={true}
            ref={aliasFormRef}
            quickSave={saveClick}
            bookingsFieldsEnabled={isEnabled}
            showEdit={showEdit}
          />
        )}
        {/* List */}
        <InmateDetailsTableContainer>
          <TableHeader>
            <h6>List of aliases</h6>
            <div className="right">
              {
                <Button
                  onClick={handleAddAssociates}
                  disabled={
                    !isEnabled ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.subModule,
                      LABELS_CAPITAL.ADD,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                      SUB_MODULES_LIST_WITH_ID.Arrest_Report
                    )
                  }
                >
                  Add Alias
                </Button>
              }
              {
                <Button
                  onClick={editAlias}
                  style={{ marginLeft: "10px" }}
                  disabled={
                    !showEdit ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.subModule,
                      LABELS_CAPITAL.VIEW,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                      SUB_MODULES_LIST_WITH_ID.Arrest_Report
                    )
                  }
                >
                  Details
                </Button>
              }
              {
                <Popconfirm
                  title="Delete the Alias"
                  description="Are you sure to delete this Alias?"
                  onConfirm={deleteAlias}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    style={{ marginLeft: "10px" }}
                    disabled={
                      !showEdit ||
                      !isEnabled ||
                      !hasCheckSecurity(
                        Security_Types.functionality,
                        Entity_types.subModule,
                        LABELS_CAPITAL.DELETE,
                        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                        SUB_MODULES_LIST_WITH_ID.Arrest_Report
                      )
                    }
                  >
                    Delete
                  </Button>
                </Popconfirm>
              }
            </div>
          </TableHeader>

          {hasCheckSecurity(
            Security_Types.functionality,
            Entity_types.subModule,
            LABELS_CAPITAL.VIEW,
            MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
            SUB_MODULES_LIST_WITH_ID.Arrest_Report
          ) && (
            <Table
              headers={[
                { key: "lName", text: "LName" },
                { key: "fName", text: "FName" },
                { key: "mName", text: "MName" },
                { key: "dob", text: "Date of Birth" },
                { key: "ssn", text: "SSN" },
                { key: "suffix", text: "Suffix" },
                { key: "questionedIdentity", text: "Questioned Identity" },
              ]}
              data={aliasData?.map((a) => ({
                ...a,
                questionedIdentity: a?.questionedIdentity ? "True" : "False",
              }))}
              onRowClick={selectAliasDataFn}
            />
          )}
        </InmateDetailsTableContainer>

        <CustomModal
          header="Alias Information"
          show={showModal}
          designPattern="ant"
          onHide={closeModal}
          onSaveClick={saveClick}
          onCloseClick={closeModal}
          submitButtonDisabled={!isEnabled}
          width={"55%"}
          bodyStyle={{ padding: "20px" }}
          isDisabled={!isDisabled && !isEnabled}
        >
          {showModal && (
            <AliasAddForm
              formData={selectAlias}
              ref={aliasFormRef}
              bookingsFieldsEnabled={isEnabled}
            />
          )}
        </CustomModal>
      </>
    );
  }
);

export default AliasesList;
